import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import request from "../../../utils/request";
import {
  appendSignature,
  getSignature,
  stripTrackingHtml,
} from "../../../utils/emailSignature";
import {
  buildInquiryMergeVars,
  detectUnresolvedVars,
  renderTemplate,
} from "./utils";
import { SafeHtml } from "../../../utils/sanitizeHtml";

// Reply composer for inquiries. Same signature+merge-var behavior as the
// leads composer, but dispatches through the inquiries thunk so the inquiry
// record (responses array, replied flag) stays in sync.
export default function InquiryReplyComposer({
  inquiry,
  adminInfo,
  onSend,
  sending,
  sendError,
}) {
  const reduxAdmin = useSelector((s) => s.login?.adminInfo || {});
  const effectiveAdmin = adminInfo || reduxAdmin;
  const ownerEmail =
    effectiveAdmin?.gmailPrimaryAddress || effectiveAdmin?.email || "";

  const [signatureHtml, setSignatureHtml] = useState(() => getSignature(ownerEmail));
  useEffect(() => {
    setSignatureHtml(getSignature(ownerEmail));
  }, [ownerEmail]);

  const aliasOptions = useMemo(
    () =>
      Array.from(
        new Set(
          [
            effectiveAdmin?.gmail,
            effectiveAdmin?.gmailPrimaryAddress,
            effectiveAdmin?.email,
            ...(effectiveAdmin?.gmailAliases || []),
          ].filter(Boolean)
        )
      ),
    [effectiveAdmin]
  );

  const defaultSubject = useMemo(() => {
    if (!inquiry) return "";
    if (inquiry.subject && /^re:/i.test(inquiry.subject)) return inquiry.subject;
    if (inquiry.subject) return `Re: ${inquiry.subject}`;
    return `Re: Your message to Bellhopt`;
  }, [inquiry]);

  const [fromAddress, setFromAddress] = useState(aliasOptions[0] || "");
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [includeSignature, setIncludeSignature] = useState(true);
  const [forceAllow, setForceAllow] = useState(false);

  useEffect(() => {
    setSubject(defaultSubject);
    setBody("");
    setSelectedTemplateId("");
    setForceAllow(false);
  }, [inquiry?._id, defaultSubject]);

  useEffect(() => {
    if (aliasOptions[0]) {
      setFromAddress((current) => current || aliasOptions[0]);
    }
  }, [aliasOptions]);

  useEffect(() => {
    let cancelled = false;
    request
      .get("/templates", { withCredentials: true })
      .then((res) => {
        if (!cancelled) setTemplates(res.data.templates || []);
      })
      .catch(() => {
        /* optional */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedTemplateId) return;
    const tpl = templates.find((t) => t._id === selectedTemplateId);
    if (tpl) {
      if (tpl.subject) setSubject(tpl.subject);
      setBody(tpl.html || tpl.body || "");
    }
  }, [selectedTemplateId, templates]);

  const mergeVars = useMemo(
    () => buildInquiryMergeVars({ inquiry, admin: effectiveAdmin }),
    [inquiry, effectiveAdmin]
  );

  const isRawText = (txt) => !/<[a-z][\s\S]*>/i.test(txt);

  const rawBodyHtml = useMemo(
    () =>
      isRawText(body)
        ? body
            .split("\n")
            .map((line, i) => (i > 0 ? `<br/>${line}` : line))
            .join("")
        : body,
    [body]
  );

  const renderedSubject = useMemo(
    () => renderTemplate(subject, mergeVars),
    [subject, mergeVars]
  );
  const renderedBody = useMemo(
    () => renderTemplate(rawBodyHtml, mergeVars),
    [rawBodyHtml, mergeVars]
  );

  const unresolved = useMemo(() => {
    const u = new Set([
      ...detectUnresolvedVars(subject, mergeVars),
      ...detectUnresolvedVars(rawBodyHtml, mergeVars),
    ]);
    return Array.from(u);
  }, [subject, rawBodyHtml, mergeVars]);

  const finalBodyHtml = useMemo(() => {
    const base = stripTrackingHtml(renderedBody);
    if (!includeSignature || !signatureHtml) return base;
    return appendSignature(base, signatureHtml);
  }, [renderedBody, includeSignature, signatureHtml]);

  const finalText = useMemo(() => {
    const plain = renderTemplate(body, mergeVars);
    if (!includeSignature || !signatureHtml) return plain;
    return `${plain}\n\n--\n${htmlToText(signatureHtml)}`.trim();
  }, [body, mergeVars, includeSignature, signatureHtml]);

  const blockSend = unresolved.length > 0 && !forceAllow;
  const canSend = Boolean(body.trim()) && Boolean(inquiry?.email);

  const handleSend = () => {
    if (!canSend || blockSend || sending) return;
    onSend?.({
      from: fromAddress,
      to: inquiry.email,
      subject: renderedSubject,
      body: finalBodyHtml,
      text: finalText,
      threadId: inquiry.threadId,
      templateUsed: selectedTemplateId,
      signatureHtml: includeSignature ? signatureHtml : "",
      disableAutoSignature: true,
    });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/80 to-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-zinc-900">Compose reply</h3>
            <p className="mt-0.5 text-xs text-zinc-500">
              Write a clear response to {inquiry?.name || inquiry?.email || "this guest"}. Templates and merge variables are supported.
            </p>
          </div>
          <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-zinc-600 ring-1 ring-zinc-200">
            To: {inquiry?.email || "—"}
          </span>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-[1fr,1fr]">
        {aliasOptions.length > 0 ? (
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              From
            </label>
            <select
              className="mt-1 w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              value={fromAddress}
              onChange={(e) => setFromAddress(e.target.value)}
            >
              {aliasOptions.map((alias) => (
                <option key={alias} value={alias}>
                  {alias}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
            Template
          </label>
          <select
            className="mt-1 w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
          >
            <option value="">— Select template —</option>
            {templates.map((t) => (
              <option key={t._id} value={t._id}>
                {t.name || t.subject}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          Subject
        </label>
        <input
          type="text"
          className="mt-1 w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
      </div>

      <div>
        <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          Body · HTML or plain text
        </label>
        <textarea
          rows={14}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write your reply. Use {{first_name|there}} for merge variables."
          className="mt-1 min-h-[280px] w-full resize-y rounded-2xl border border-zinc-300 bg-white px-4 py-3 text-[15px] leading-6 text-zinc-900 placeholder:text-zinc-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
        />
        <div className="mt-1 flex items-center justify-between text-[11px] text-zinc-500">
          <span>Tip: plain text keeps line breaks; HTML is supported for richer replies.</span>
          <span>{body.trim().length} chars</span>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-xs text-zinc-700">
        <label className="inline-flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={includeSignature}
            onChange={(e) => setIncludeSignature(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-zinc-300 text-indigo-600"
          />
          <span>
            Append my signature
            {!signatureHtml ? (
              <span className="ml-1 text-red-600">(save one in Settings → Email signature)</span>
            ) : null}
          </span>
        </label>
        <span className="text-[11px] text-zinc-500">Tracking pixels stripped automatically.</span>
      </div>

      {unresolved.length > 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs leading-5 text-amber-900">
          <p className="font-semibold">
            {unresolved.length === 1
              ? "1 merge variable is empty for this inquiry:"
              : `${unresolved.length} merge variables are empty for this inquiry:`}
          </p>
          <p className="mt-0.5">
            {unresolved.map((key) => (
              <code
                key={key}
                className="mr-1 rounded bg-amber-100 px-1 py-0.5 font-mono text-[11px]"
              >{`{{${key}}}`}</code>
            ))}
          </p>
          <p className="mt-1">
            Add a fallback like{" "}
            <code className="rounded bg-white px-1 py-0.5 font-mono text-[11px]">{`{{first_name|there}}`}</code>
            , or{" "}
            <button
              type="button"
              onClick={() => setForceAllow((v) => !v)}
              className="font-semibold underline"
            >
              {forceAllow ? "re-enable safety check" : "send anyway"}
            </button>
            .
          </p>
        </div>
      ) : null}

      {(body || includeSignature) && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
          <div className="mb-1.5 flex items-center justify-between">
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              Preview
            </h4>
            <span className="text-[11px] text-zinc-500">
              To: <span className="font-medium text-zinc-800">{inquiry?.email || "—"}</span>
            </span>
          </div>
          <div className="border-b border-zinc-100 pb-1.5 text-xs">
            <span className="mr-1 font-semibold text-zinc-700">Subject:</span>
            <span className="text-zinc-900">
              {renderedSubject || <em className="text-zinc-400">(empty)</em>}
            </span>
          </div>
          {finalBodyHtml ? (
            <SafeHtml
              value={finalBodyHtml}
              assumeHtml
              className="prose prose-sm mt-3 max-h-80 max-w-none overflow-y-auto rounded-xl bg-zinc-50 p-3 text-sm leading-relaxed text-zinc-800 prose-a:text-indigo-600 prose-p:my-1.5"
            />
          ) : (
            <p className="mt-3 rounded-xl bg-zinc-50 p-3 text-sm italic text-zinc-400">
              Empty body
            </p>
          )}
        </div>
      )}

      {sendError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-700">
          {String(sendError)}
        </div>
      ) : null}

      <div className="sticky bottom-0 -mx-1 flex items-center justify-end gap-2 border-t border-zinc-100 bg-white/95 px-1 py-3 backdrop-blur">
        <button
          type="button"
          onClick={handleSend}
          disabled={sending || !canSend || blockSend}
          className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-zinc-300"
        >
          {sending
            ? "Sending…"
            : blockSend
            ? "Fix merge variables to send"
            : "Send reply"}
        </button>
      </div>
    </div>
  );
}

function htmlToText(html) {
  if (!html) return "";
  if (typeof document !== "undefined") {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || "").replace(/\s+\n/g, "\n").trim();
  }
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}
